export type Disambiguation = 'earlier' | 'later'

export interface TimeZoneState {
  date: string
  time: string
  sourceZone: string
  zones: string[]
  disambiguation: Disambiguation
}

export interface ZonedInstantResult {
  status: 'ready' | 'ambiguous' | 'gap' | 'invalid'
  instants: number[]
  instant: number | null
}

interface WallClock {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

const HOUR_MS = 3_600_000
const DEFAULT_ZONE = 'UTC'
const SSG_DATE = '2026-01-01'
const SSG_TIME = '12:00'
const MAX_SHARED_STATE_LENGTH = 2_048
const MAX_SHARED_ZONE_TOKENS = 5
const AMBIGUOUS_TIME_ZONE_ABBREVIATIONS = new Set(['CST', 'EST', 'HST', 'IST', 'MST', 'PST'])
const UNAMBIGUOUS_TIME_ZONE_ALIASES = new Set(['UTC', 'GMT', 'UCT', 'ZULU', 'GB', 'NZ'])

export const CURATED_TIME_ZONES = [
  { zone: 'UTC', ru: 'UTC, Всемирное координированное время', en: 'UTC, Coordinated Universal Time' },
  { zone: 'Europe/Moscow', ru: 'Москва', en: 'Moscow' },
  { zone: 'Europe/Kaliningrad', ru: 'Калининград', en: 'Kaliningrad' },
  { zone: 'Europe/Samara', ru: 'Самара', en: 'Samara' },
  { zone: 'Asia/Yekaterinburg', ru: 'Екатеринбург', en: 'Yekaterinburg' },
  { zone: 'Asia/Omsk', ru: 'Омск', en: 'Omsk' },
  { zone: 'Asia/Novosibirsk', ru: 'Новосибирск', en: 'Novosibirsk' },
  { zone: 'Asia/Irkutsk', ru: 'Иркутск', en: 'Irkutsk' },
  { zone: 'Asia/Yakutsk', ru: 'Якутск', en: 'Yakutsk' },
  { zone: 'Asia/Vladivostok', ru: 'Владивосток', en: 'Vladivostok' },
  { zone: 'Asia/Magadan', ru: 'Магадан', en: 'Magadan' },
  { zone: 'Asia/Kamchatka', ru: 'Камчатка, Петропавловск-Камчатский', en: 'Kamchatka, Petropavlovsk-Kamchatsky' },
  { zone: 'Europe/London', ru: 'Лондон', en: 'London' },
  { zone: 'Europe/Paris', ru: 'Париж', en: 'Paris' },
  { zone: 'Europe/Berlin', ru: 'Берлин', en: 'Berlin' },
  { zone: 'Europe/Istanbul', ru: 'Стамбул', en: 'Istanbul' },
  { zone: 'America/New_York', ru: 'Нью-Йорк', en: 'New York' },
  { zone: 'America/Chicago', ru: 'Чикаго', en: 'Chicago' },
  { zone: 'America/Denver', ru: 'Денвер', en: 'Denver' },
  { zone: 'America/Los_Angeles', ru: 'Лос-Анджелес', en: 'Los Angeles' },
  { zone: 'America/Toronto', ru: 'Торонто', en: 'Toronto' },
  { zone: 'America/Sao_Paulo', ru: 'Сан-Паулу', en: 'Sao Paulo' },
  { zone: 'Asia/Dubai', ru: 'Дубай', en: 'Dubai' },
  { zone: 'Asia/Kolkata', ru: 'Дели, Мумбаи, Индия', en: 'Delhi, Mumbai, India' },
  { zone: 'Asia/Kathmandu', ru: 'Катманду, Непал', en: 'Kathmandu, Nepal' },
  { zone: 'Asia/Bangkok', ru: 'Бангкок', en: 'Bangkok' },
  { zone: 'Asia/Singapore', ru: 'Сингапур', en: 'Singapore' },
  { zone: 'Asia/Shanghai', ru: 'Пекин, Шанхай', en: 'Beijing, Shanghai' },
  { zone: 'Asia/Tokyo', ru: 'Токио', en: 'Tokyo' },
  { zone: 'Asia/Seoul', ru: 'Сеул', en: 'Seoul' },
  { zone: 'Australia/Sydney', ru: 'Сидней', en: 'Sydney' },
  { zone: 'Pacific/Auckland', ru: 'Окленд', en: 'Auckland' },
] as const

export function resolveCuratedTimeZone(query: string): string | null {
  const normalized = query.trim().toLowerCase()
  if (!normalized) return null
  const entry = CURATED_TIME_ZONES.find((candidate) => {
    const labels = [candidate.zone, ...candidate.ru.split(','), ...candidate.en.split(',')]
    return labels.some((label) => label.trim().toLowerCase() === normalized)
  })
  return entry?.zone ?? null
}

const PREFERRED_TIME_ZONE_ALIASES: ReadonlyArray<readonly [string, readonly string[]]> = [
  ...CURATED_TIME_ZONES.map((entry) => [entry.zone, []] as const),
  ['Asia/Kolkata', ['Asia/Calcutta']],
  ['Asia/Kathmandu', ['Asia/Katmandu']],
  ['Europe/Kyiv', ['Europe/Kiev']],
  ['America/Nuuk', ['America/Godthab']],
  ['Asia/Yangon', ['Asia/Rangoon']],
  ['Asia/Ho_Chi_Minh', ['Asia/Saigon']],
]

const EXPLICIT_TIME_ZONE_ALIASES = new Map([
  ['utc', 'UTC'], ['gmt', 'UTC'], ['uct', 'UTC'], ['zulu', 'UTC'],
  ['gb', 'Europe/London'], ['nz', 'Pacific/Auckland'],
  ['etc/utc', 'UTC'], ['etc/uct', 'UTC'], ['etc/universal', 'UTC'], ['etc/zulu', 'UTC'], ['etc/gmt', 'UTC'],
  ['etc/gmt0', 'UTC'], ['etc/greenwich', 'UTC'],
  ['us/eastern', 'America/New_York'], ['us/central', 'America/Chicago'],
  ['us/mountain', 'America/Denver'], ['us/pacific', 'America/Los_Angeles'],
  ['asia/calcutta', 'Asia/Kolkata'],
  ['asia/katmandu', 'Asia/Kathmandu'], ['europe/kiev', 'Europe/Kyiv'],
])

const IANA_PRIMARY_TIME_ZONE_IDS = new Set<string>([
  'Europe/Andorra',
  'Asia/Dubai',
  'Asia/Kabul',
  'Europe/Tirane',
  'Asia/Yerevan',
  'Antarctica/Casey',
  'Antarctica/Davis',
  'Antarctica/DumontDUrville',
  'Antarctica/Mawson',
  'Antarctica/Palmer',
  'Antarctica/Rothera',
  'Antarctica/Syowa',
  'Antarctica/Troll',
  'Antarctica/Vostok',
  'America/Argentina/Buenos_Aires',
  'America/Argentina/Cordoba',
  'America/Argentina/Salta',
  'America/Argentina/Jujuy',
  'America/Argentina/Tucuman',
  'America/Argentina/Catamarca',
  'America/Argentina/La_Rioja',
  'America/Argentina/San_Juan',
  'America/Argentina/Mendoza',
  'America/Argentina/San_Luis',
  'America/Argentina/Rio_Gallegos',
  'America/Argentina/Ushuaia',
  'Pacific/Pago_Pago',
  'Europe/Vienna',
  'Australia/Lord_Howe',
  'Antarctica/Macquarie',
  'Australia/Hobart',
  'Australia/Melbourne',
  'Australia/Sydney',
  'Australia/Broken_Hill',
  'Australia/Brisbane',
  'Australia/Lindeman',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Perth',
  'Australia/Eucla',
  'Asia/Baku',
  'America/Barbados',
  'Asia/Dhaka',
  'Europe/Brussels',
  'Europe/Sofia',
  'Atlantic/Bermuda',
  'America/La_Paz',
  'America/Noronha',
  'America/Belem',
  'America/Fortaleza',
  'America/Recife',
  'America/Araguaina',
  'America/Maceio',
  'America/Bahia',
  'America/Sao_Paulo',
  'America/Campo_Grande',
  'America/Cuiaba',
  'America/Santarem',
  'America/Porto_Velho',
  'America/Boa_Vista',
  'America/Manaus',
  'America/Eirunepe',
  'America/Rio_Branco',
  'Asia/Thimphu',
  'Europe/Minsk',
  'America/Belize',
  'America/St_Johns',
  'America/Halifax',
  'America/Glace_Bay',
  'America/Moncton',
  'America/Goose_Bay',
  'America/Toronto',
  'America/Iqaluit',
  'America/Winnipeg',
  'America/Resolute',
  'America/Rankin_Inlet',
  'America/Regina',
  'America/Swift_Current',
  'America/Edmonton',
  'America/Cambridge_Bay',
  'America/Inuvik',
  'America/Vancouver',
  'America/Dawson_Creek',
  'America/Fort_Nelson',
  'America/Whitehorse',
  'America/Dawson',
  'Europe/Zurich',
  'Africa/Abidjan',
  'Pacific/Rarotonga',
  'America/Santiago',
  'America/Coyhaique',
  'America/Punta_Arenas',
  'Pacific/Easter',
  'Asia/Shanghai',
  'Asia/Urumqi',
  'America/Bogota',
  'America/Costa_Rica',
  'America/Havana',
  'Atlantic/Cape_Verde',
  'Asia/Nicosia',
  'Asia/Famagusta',
  'Europe/Prague',
  'Europe/Berlin',
  'America/Santo_Domingo',
  'Africa/Algiers',
  'America/Guayaquil',
  'Pacific/Galapagos',
  'Europe/Tallinn',
  'Africa/Cairo',
  'Africa/El_Aaiun',
  'Europe/Madrid',
  'Africa/Ceuta',
  'Atlantic/Canary',
  'Europe/Helsinki',
  'Pacific/Fiji',
  'Atlantic/Stanley',
  'Pacific/Kosrae',
  'Atlantic/Faroe',
  'Europe/Paris',
  'Europe/London',
  'Asia/Tbilisi',
  'America/Cayenne',
  'Europe/Gibraltar',
  'America/Nuuk',
  'America/Danmarkshavn',
  'America/Scoresbysund',
  'America/Thule',
  'Europe/Athens',
  'Atlantic/South_Georgia',
  'America/Guatemala',
  'Pacific/Guam',
  'Africa/Bissau',
  'America/Guyana',
  'Asia/Hong_Kong',
  'America/Tegucigalpa',
  'America/Port-au-Prince',
  'Europe/Budapest',
  'Asia/Jakarta',
  'Asia/Pontianak',
  'Asia/Makassar',
  'Asia/Jayapura',
  'Europe/Dublin',
  'Asia/Jerusalem',
  'Asia/Kolkata',
  'Indian/Chagos',
  'Indian/Christmas',
  'Indian/Cocos',
  'Asia/Baghdad',
  'Asia/Tehran',
  'Europe/Rome',
  'America/Jamaica',
  'Asia/Amman',
  'Asia/Tokyo',
  'Africa/Nairobi',
  'Asia/Bishkek',
  'Pacific/Tarawa',
  'Pacific/Kanton',
  'Pacific/Kiritimati',
  'Asia/Pyongyang',
  'Asia/Seoul',
  'Asia/Almaty',
  'Asia/Qyzylorda',
  'Asia/Qostanay',
  'Asia/Aqtobe',
  'Asia/Aqtau',
  'Asia/Atyrau',
  'Asia/Oral',
  'Asia/Beirut',
  'Asia/Colombo',
  'Africa/Monrovia',
  'Europe/Vilnius',
  'Europe/Riga',
  'Africa/Tripoli',
  'Africa/Casablanca',
  'Europe/Chisinau',
  'Pacific/Kwajalein',
  'Asia/Yangon',
  'Asia/Ulaanbaatar',
  'Asia/Hovd',
  'Asia/Macau',
  'America/Martinique',
  'Europe/Malta',
  'Indian/Mauritius',
  'Indian/Maldives',
  'America/Mexico_City',
  'America/Cancun',
  'America/Merida',
  'America/Monterrey',
  'America/Matamoros',
  'America/Chihuahua',
  'America/Ciudad_Juarez',
  'America/Ojinaga',
  'America/Mazatlan',
  'America/Bahia_Banderas',
  'America/Hermosillo',
  'America/Tijuana',
  'Asia/Kuching',
  'Africa/Maputo',
  'Africa/Windhoek',
  'Pacific/Noumea',
  'Pacific/Norfolk',
  'Africa/Lagos',
  'America/Managua',
  'Asia/Kathmandu',
  'Pacific/Nauru',
  'Pacific/Niue',
  'Pacific/Auckland',
  'Pacific/Chatham',
  'America/Panama',
  'America/Lima',
  'Pacific/Tahiti',
  'Pacific/Marquesas',
  'Pacific/Gambier',
  'Pacific/Port_Moresby',
  'Pacific/Bougainville',
  'Asia/Manila',
  'Asia/Karachi',
  'Europe/Warsaw',
  'America/Miquelon',
  'Pacific/Pitcairn',
  'America/Puerto_Rico',
  'Asia/Gaza',
  'Asia/Hebron',
  'Europe/Lisbon',
  'Atlantic/Madeira',
  'Atlantic/Azores',
  'Pacific/Palau',
  'America/Asuncion',
  'Asia/Qatar',
  'Europe/Bucharest',
  'Europe/Belgrade',
  'Europe/Kaliningrad',
  'Europe/Moscow',
  'Europe/Simferopol',
  'Europe/Kirov',
  'Europe/Volgograd',
  'Europe/Astrakhan',
  'Europe/Saratov',
  'Europe/Ulyanovsk',
  'Europe/Samara',
  'Asia/Yekaterinburg',
  'Asia/Omsk',
  'Asia/Novosibirsk',
  'Asia/Barnaul',
  'Asia/Tomsk',
  'Asia/Novokuznetsk',
  'Asia/Krasnoyarsk',
  'Asia/Irkutsk',
  'Asia/Chita',
  'Asia/Yakutsk',
  'Asia/Khandyga',
  'Asia/Vladivostok',
  'Asia/Ust-Nera',
  'Asia/Magadan',
  'Asia/Sakhalin',
  'Asia/Srednekolymsk',
  'Asia/Kamchatka',
  'Asia/Anadyr',
  'Asia/Riyadh',
  'Pacific/Guadalcanal',
  'Africa/Khartoum',
  'Asia/Singapore',
  'America/Paramaribo',
  'Africa/Juba',
  'Africa/Sao_Tome',
  'America/El_Salvador',
  'Asia/Damascus',
  'America/Grand_Turk',
  'Africa/Ndjamena',
  'Asia/Bangkok',
  'Asia/Dushanbe',
  'Pacific/Fakaofo',
  'Asia/Dili',
  'Asia/Ashgabat',
  'Africa/Tunis',
  'Pacific/Tongatapu',
  'Europe/Istanbul',
  'Asia/Taipei',
  'Europe/Kyiv',
  'America/New_York',
  'America/Detroit',
  'America/Kentucky/Louisville',
  'America/Kentucky/Monticello',
  'America/Indiana/Indianapolis',
  'America/Indiana/Vincennes',
  'America/Indiana/Winamac',
  'America/Indiana/Marengo',
  'America/Indiana/Petersburg',
  'America/Indiana/Vevay',
  'America/Chicago',
  'America/Indiana/Tell_City',
  'America/Indiana/Knox',
  'America/Menominee',
  'America/North_Dakota/Center',
  'America/North_Dakota/New_Salem',
  'America/North_Dakota/Beulah',
  'America/Denver',
  'America/Boise',
  'America/Phoenix',
  'America/Los_Angeles',
  'America/Anchorage',
  'America/Juneau',
  'America/Sitka',
  'America/Metlakatla',
  'America/Yakutat',
  'America/Nome',
  'America/Adak',
  'Pacific/Honolulu',
  'America/Montevideo',
  'Asia/Samarkand',
  'Asia/Tashkent',
  'America/Caracas',
  'Asia/Ho_Chi_Minh',
  'Pacific/Efate',
  'Pacific/Apia',
  'Africa/Johannesburg',
])

// Additional Zone records from the default tzdata.zi profile (the optional
// backzone profile is intentionally not mixed into this compatibility policy).
const TZDATA_2026B_PRIMARY_TIME_ZONE_IDS = new Set([
  'Africa/Accra', 'Africa/Addis_Ababa', 'Africa/Asmara', 'Africa/Bamako',
  'Africa/Bangui', 'Africa/Banjul', 'Africa/Blantyre', 'Africa/Brazzaville',
  'Africa/Bujumbura', 'Africa/Conakry', 'Africa/Dakar', 'Africa/Dar_es_Salaam',
  'Africa/Djibouti', 'Africa/Douala', 'Africa/Freetown', 'Africa/Gaborone',
  'Africa/Harare', 'Africa/Kampala', 'Africa/Kigali', 'Africa/Kinshasa',
  'Africa/Libreville', 'Africa/Lome', 'Africa/Luanda', 'Africa/Lubumbashi',
  'Africa/Lusaka', 'Africa/Malabo', 'Africa/Maseru', 'Africa/Mbabane',
  'Africa/Mogadishu', 'Africa/Niamey', 'Africa/Nouakchott', 'Africa/Ouagadougou',
  'Africa/Porto-Novo', 'America/Anguilla', 'America/Antigua', 'America/Aruba',
  'America/Atikokan', 'America/Blanc-Sablon', 'America/Cayman', 'America/Creston',
  'America/Curacao', 'America/Dominica', 'America/Grenada', 'America/Guadeloupe',
  'America/Montserrat', 'America/Nassau', 'America/Port_of_Spain',
  'America/St_Kitts', 'America/St_Lucia', 'America/St_Thomas', 'America/St_Vincent',
  'America/Tortola', 'Antarctica/McMurdo', 'Asia/Aden', 'Asia/Bahrain',
  'Asia/Brunei', 'Asia/Kuala_Lumpur', 'Asia/Kuwait', 'Asia/Muscat',
  'Asia/Phnom_Penh', 'Asia/Vientiane', 'Atlantic/Reykjavik', 'Atlantic/St_Helena',
  'CET', 'CST6CDT', 'EET', 'EST', 'EST5EDT', 'Etc/GMT', 'Etc/GMT+1',
  'Etc/GMT+10', 'Etc/GMT+11', 'Etc/GMT+12', 'Etc/GMT+2', 'Etc/GMT+3',
  'Etc/GMT+4', 'Etc/GMT+5', 'Etc/GMT+6', 'Etc/GMT+7', 'Etc/GMT+8', 'Etc/GMT+9',
  'Etc/GMT-1', 'Etc/GMT-10', 'Etc/GMT-11', 'Etc/GMT-12', 'Etc/GMT-13',
  'Etc/GMT-14', 'Etc/GMT-2', 'Etc/GMT-3', 'Etc/GMT-4', 'Etc/GMT-5', 'Etc/GMT-6',
  'Etc/GMT-7', 'Etc/GMT-8', 'Etc/GMT-9', 'Etc/UTC', 'Europe/Amsterdam',
  'Europe/Copenhagen', 'Europe/Guernsey', 'Europe/Isle_of_Man', 'Europe/Jersey',
  'Europe/Ljubljana', 'Europe/Luxembourg', 'Europe/Monaco', 'Europe/Oslo',
  'Europe/Sarajevo', 'Europe/Skopje', 'Europe/Stockholm', 'Europe/Vaduz',
  'Europe/Zagreb', 'Factory', 'HST', 'Indian/Antananarivo', 'Indian/Comoro',
  'Indian/Kerguelen', 'Indian/Mahe', 'Indian/Mayotte', 'Indian/Reunion', 'MET',
  'MST', 'MST7MDT', 'PST8PDT', 'Pacific/Chuuk', 'Pacific/Funafuti',
  'Pacific/Majuro', 'Pacific/Midway', 'Pacific/Pohnpei', 'Pacific/Saipan',
  'Pacific/Wake', 'Pacific/Wallis', 'WET',
])

const IANA_LINK_TO_PREFERRED = new Map<string, string>([
  ['australia/act', 'Australia/Sydney'],
  ['australia/lhi', 'Australia/Lord_Howe'],
  ['australia/nsw', 'Australia/Sydney'],
  ['australia/north', 'Australia/Darwin'],
  ['australia/queensland', 'Australia/Brisbane'],
  ['australia/south', 'Australia/Adelaide'],
  ['australia/tasmania', 'Australia/Hobart'],
  ['australia/victoria', 'Australia/Melbourne'],
  ['australia/west', 'Australia/Perth'],
  ['australia/yancowinna', 'Australia/Broken_Hill'],
  ['brazil/acre', 'America/Rio_Branco'],
  ['brazil/denoronha', 'America/Noronha'],
  ['brazil/east', 'America/Sao_Paulo'],
  ['brazil/west', 'America/Manaus'],
  ['cet', 'Europe/Brussels'],
  ['cst6cdt', 'America/Chicago'],
  ['canada/atlantic', 'America/Halifax'],
  ['canada/central', 'America/Winnipeg'],
  ['canada/eastern', 'America/Toronto'],
  ['canada/mountain', 'America/Edmonton'],
  ['canada/newfoundland', 'America/St_Johns'],
  ['canada/pacific', 'America/Vancouver'],
  ['canada/saskatchewan', 'America/Regina'],
  ['canada/yukon', 'America/Whitehorse'],
  ['chile/continental', 'America/Santiago'],
  ['chile/easterisland', 'Pacific/Easter'],
  ['cuba', 'America/Havana'],
  ['eet', 'Europe/Athens'],
  ['est', 'America/Panama'],
  ['est5edt', 'America/New_York'],
  ['egypt', 'Africa/Cairo'],
  ['eire', 'Europe/Dublin'],
  ['etc/gmt+0', 'Etc/GMT'],
  ['etc/gmt-0', 'Etc/GMT'],
  ['etc/gmt0', 'Etc/GMT'],
  ['etc/greenwich', 'Etc/GMT'],
  ['etc/uct', 'Etc/UTC'],
  ['etc/universal', 'Etc/UTC'],
  ['etc/zulu', 'Etc/UTC'],
  ['gb', 'Europe/London'],
  ['gb-eire', 'Europe/London'],
  ['gmt+0', 'Etc/GMT'],
  ['gmt-0', 'Etc/GMT'],
  ['gmt0', 'Etc/GMT'],
  ['greenwich', 'Etc/GMT'],
  ['hongkong', 'Asia/Hong_Kong'],
  ['iceland', 'Africa/Abidjan'],
  ['iran', 'Asia/Tehran'],
  ['israel', 'Asia/Jerusalem'],
  ['jamaica', 'America/Jamaica'],
  ['japan', 'Asia/Tokyo'],
  ['kwajalein', 'Pacific/Kwajalein'],
  ['libya', 'Africa/Tripoli'],
  ['met', 'Europe/Brussels'],
  ['mst', 'America/Phoenix'],
  ['mst7mdt', 'America/Denver'],
  ['mexico/bajanorte', 'America/Tijuana'],
  ['mexico/bajasur', 'America/Mazatlan'],
  ['mexico/general', 'America/Mexico_City'],
  ['nz', 'Pacific/Auckland'],
  ['nz-chat', 'Pacific/Chatham'],
  ['navajo', 'America/Denver'],
  ['prc', 'Asia/Shanghai'],
  ['poland', 'Europe/Warsaw'],
  ['portugal', 'Europe/Lisbon'],
  ['roc', 'Asia/Taipei'],
  ['rok', 'Asia/Seoul'],
  ['singapore', 'Asia/Singapore'],
  ['turkey', 'Europe/Istanbul'],
  ['uct', 'Etc/UTC'],
  ['us/alaska', 'America/Anchorage'],
  ['us/aleutian', 'America/Adak'],
  ['us/arizona', 'America/Phoenix'],
  ['us/central', 'America/Chicago'],
  ['us/east-indiana', 'America/Indiana/Indianapolis'],
  ['us/eastern', 'America/New_York'],
  ['us/hawaii', 'Pacific/Honolulu'],
  ['us/indiana-starke', 'America/Indiana/Knox'],
  ['us/michigan', 'America/Detroit'],
  ['us/mountain', 'America/Denver'],
  ['us/pacific', 'America/Los_Angeles'],
  ['us/samoa', 'Pacific/Pago_Pago'],
  ['utc', 'Etc/UTC'],
  ['universal', 'Etc/UTC'],
  ['w-su', 'Europe/Moscow'],
  ['zulu', 'Etc/UTC'],
  ['america/buenos_aires', 'America/Argentina/Buenos_Aires'],
  ['america/catamarca', 'America/Argentina/Catamarca'],
  ['america/cordoba', 'America/Argentina/Cordoba'],
  ['america/indianapolis', 'America/Indiana/Indianapolis'],
  ['america/jujuy', 'America/Argentina/Jujuy'],
  ['america/knox_in', 'America/Indiana/Knox'],
  ['america/louisville', 'America/Kentucky/Louisville'],
  ['america/mendoza', 'America/Argentina/Mendoza'],
  ['america/virgin', 'America/Puerto_Rico'],
  ['pacific/samoa', 'Pacific/Pago_Pago'],
  ['africa/accra', 'Africa/Abidjan'],
  ['africa/addis_ababa', 'Africa/Nairobi'],
  ['africa/asmara', 'Africa/Nairobi'],
  ['africa/bamako', 'Africa/Abidjan'],
  ['africa/bangui', 'Africa/Lagos'],
  ['africa/banjul', 'Africa/Abidjan'],
  ['africa/blantyre', 'Africa/Maputo'],
  ['africa/brazzaville', 'Africa/Lagos'],
  ['africa/bujumbura', 'Africa/Maputo'],
  ['africa/conakry', 'Africa/Abidjan'],
  ['africa/dakar', 'Africa/Abidjan'],
  ['africa/dar_es_salaam', 'Africa/Nairobi'],
  ['africa/djibouti', 'Africa/Nairobi'],
  ['africa/douala', 'Africa/Lagos'],
  ['africa/freetown', 'Africa/Abidjan'],
  ['africa/gaborone', 'Africa/Maputo'],
  ['africa/harare', 'Africa/Maputo'],
  ['africa/kampala', 'Africa/Nairobi'],
  ['africa/kigali', 'Africa/Maputo'],
  ['africa/kinshasa', 'Africa/Lagos'],
  ['africa/libreville', 'Africa/Lagos'],
  ['africa/lome', 'Africa/Abidjan'],
  ['africa/luanda', 'Africa/Lagos'],
  ['africa/lubumbashi', 'Africa/Maputo'],
  ['africa/lusaka', 'Africa/Maputo'],
  ['africa/malabo', 'Africa/Lagos'],
  ['africa/maseru', 'Africa/Johannesburg'],
  ['africa/mbabane', 'Africa/Johannesburg'],
  ['africa/mogadishu', 'Africa/Nairobi'],
  ['africa/niamey', 'Africa/Lagos'],
  ['africa/nouakchott', 'Africa/Abidjan'],
  ['africa/ouagadougou', 'Africa/Abidjan'],
  ['africa/porto-novo', 'Africa/Lagos'],
  ['america/anguilla', 'America/Puerto_Rico'],
  ['america/antigua', 'America/Puerto_Rico'],
  ['america/aruba', 'America/Puerto_Rico'],
  ['america/atikokan', 'America/Panama'],
  ['america/blanc-sablon', 'America/Puerto_Rico'],
  ['america/cayman', 'America/Panama'],
  ['america/creston', 'America/Phoenix'],
  ['america/curacao', 'America/Puerto_Rico'],
  ['america/dominica', 'America/Puerto_Rico'],
  ['america/grenada', 'America/Puerto_Rico'],
  ['america/guadeloupe', 'America/Puerto_Rico'],
  ['america/kralendijk', 'America/Puerto_Rico'],
  ['america/lower_princes', 'America/Puerto_Rico'],
  ['america/marigot', 'America/Puerto_Rico'],
  ['america/montserrat', 'America/Puerto_Rico'],
  ['america/nassau', 'America/Toronto'],
  ['america/port_of_spain', 'America/Puerto_Rico'],
  ['america/st_barthelemy', 'America/Puerto_Rico'],
  ['america/st_kitts', 'America/Puerto_Rico'],
  ['america/st_lucia', 'America/Puerto_Rico'],
  ['america/st_thomas', 'America/Puerto_Rico'],
  ['america/st_vincent', 'America/Puerto_Rico'],
  ['america/tortola', 'America/Puerto_Rico'],
  ['antarctica/dumontdurville', 'Pacific/Port_Moresby'],
  ['antarctica/mcmurdo', 'Pacific/Auckland'],
  ['antarctica/syowa', 'Asia/Riyadh'],
  ['arctic/longyearbyen', 'Europe/Berlin'],
  ['asia/aden', 'Asia/Riyadh'],
  ['asia/bahrain', 'Asia/Qatar'],
  ['asia/brunei', 'Asia/Kuching'],
  ['asia/kuala_lumpur', 'Asia/Singapore'],
  ['asia/kuwait', 'Asia/Riyadh'],
  ['asia/muscat', 'Asia/Dubai'],
  ['asia/phnom_penh', 'Asia/Bangkok'],
  ['asia/vientiane', 'Asia/Bangkok'],
  ['atlantic/reykjavik', 'Africa/Abidjan'],
  ['atlantic/st_helena', 'Africa/Abidjan'],
  ['europe/amsterdam', 'Europe/Brussels'],
  ['europe/bratislava', 'Europe/Prague'],
  ['europe/busingen', 'Europe/Zurich'],
  ['europe/copenhagen', 'Europe/Berlin'],
  ['europe/guernsey', 'Europe/London'],
  ['europe/isle_of_man', 'Europe/London'],
  ['europe/jersey', 'Europe/London'],
  ['europe/ljubljana', 'Europe/Belgrade'],
  ['europe/luxembourg', 'Europe/Brussels'],
  ['europe/mariehamn', 'Europe/Helsinki'],
  ['europe/monaco', 'Europe/Paris'],
  ['europe/oslo', 'Europe/Berlin'],
  ['europe/podgorica', 'Europe/Belgrade'],
  ['europe/san_marino', 'Europe/Rome'],
  ['europe/sarajevo', 'Europe/Belgrade'],
  ['europe/skopje', 'Europe/Belgrade'],
  ['europe/stockholm', 'Europe/Berlin'],
  ['europe/vaduz', 'Europe/Zurich'],
  ['europe/vatican', 'Europe/Rome'],
  ['europe/zagreb', 'Europe/Belgrade'],
  ['indian/antananarivo', 'Africa/Nairobi'],
  ['indian/christmas', 'Asia/Bangkok'],
  ['indian/cocos', 'Asia/Yangon'],
  ['indian/comoro', 'Africa/Nairobi'],
  ['indian/kerguelen', 'Indian/Maldives'],
  ['indian/mahe', 'Asia/Dubai'],
  ['indian/mayotte', 'Africa/Nairobi'],
  ['indian/reunion', 'Asia/Dubai'],
  ['pacific/chuuk', 'Pacific/Chuuk'],
  ['pacific/funafuti', 'Pacific/Tarawa'],
  ['pacific/majuro', 'Pacific/Tarawa'],
  ['pacific/midway', 'Pacific/Pago_Pago'],
  ['pacific/pohnpei', 'Pacific/Guadalcanal'],
  ['pacific/saipan', 'Pacific/Guam'],
  ['pacific/wake', 'Pacific/Tarawa'],
  ['pacific/wallis', 'Pacific/Tarawa'],
  ['africa/timbuktu', 'Africa/Abidjan'],
  ['america/argentina/comodrivadavia', 'America/Argentina/Catamarca'],
  ['america/atka', 'America/Adak'],
  ['america/coral_harbour', 'America/Panama'],
  ['america/ensenada', 'America/Tijuana'],
  ['america/fort_wayne', 'America/Indiana/Indianapolis'],
  ['america/montreal', 'America/Toronto'],
  ['america/nipigon', 'America/Toronto'],
  ['america/pangnirtung', 'America/Iqaluit'],
  ['america/porto_acre', 'America/Rio_Branco'],
  ['america/rainy_river', 'America/Winnipeg'],
  ['america/rosario', 'America/Argentina/Cordoba'],
  ['america/santa_isabel', 'America/Tijuana'],
  ['america/shiprock', 'America/Denver'],
  ['america/thunder_bay', 'America/Toronto'],
  ['america/yellowknife', 'America/Edmonton'],
  ['antarctica/south_pole', 'Pacific/Auckland'],
  ['asia/choibalsan', 'Asia/Ulaanbaatar'],
  ['asia/chongqing', 'Asia/Shanghai'],
  ['asia/harbin', 'Asia/Shanghai'],
  ['asia/kashgar', 'Asia/Urumqi'],
  ['asia/tel_aviv', 'Asia/Jerusalem'],
  ['atlantic/jan_mayen', 'Europe/Berlin'],
  ['australia/canberra', 'Australia/Sydney'],
  ['australia/currie', 'Australia/Hobart'],
  ['europe/belfast', 'Europe/London'],
  ['europe/tiraspol', 'Europe/Chisinau'],
  ['europe/uzhgorod', 'Europe/Kyiv'],
  ['europe/zaporozhye', 'Europe/Kyiv'],
  ['pacific/enderbury', 'Pacific/Kanton'],
  ['pacific/johnston', 'Pacific/Honolulu'],
  ['pacific/yap', 'Pacific/Chuuk'],
  ['wet', 'Europe/Lisbon'],
  ['africa/asmera', 'Africa/Nairobi'],
  ['america/godthab', 'America/Nuuk'],
  ['asia/ashkhabad', 'Asia/Ashgabat'],
  ['asia/calcutta', 'Asia/Kolkata'],
  ['asia/chungking', 'Asia/Shanghai'],
  ['asia/dacca', 'Asia/Dhaka'],
  ['asia/istanbul', 'Europe/Istanbul'],
  ['asia/katmandu', 'Asia/Kathmandu'],
  ['asia/macao', 'Asia/Macau'],
  ['asia/rangoon', 'Asia/Yangon'],
  ['asia/saigon', 'Asia/Ho_Chi_Minh'],
  ['asia/thimbu', 'Asia/Thimphu'],
  ['asia/ujung_pandang', 'Asia/Makassar'],
  ['asia/ulan_bator', 'Asia/Ulaanbaatar'],
  ['atlantic/faeroe', 'Atlantic/Faroe'],
  ['europe/kiev', 'Europe/Kyiv'],
  ['europe/nicosia', 'Asia/Nicosia'],
  ['hst', 'Pacific/Honolulu'],
  ['pst8pdt', 'America/Los_Angeles'],
  ['pacific/ponape', 'Pacific/Guadalcanal'],
  ['pacific/truk', 'Pacific/Port_Moresby'],
  ['africa/asmera', 'Africa/Asmara'],
  ['africa/asmara', 'Africa/Asmara'],
  ['pacific/truk', 'Pacific/Chuuk'],
  ['pacific/ponape', 'Pacific/Pohnpei'],
  ['pacific/chuuk', 'Pacific/Chuuk'],
  ['pacific/pohnpei', 'Pacific/Pohnpei'],
  ['pacific/enderbury', 'Pacific/Kanton'],
  ['pacific/kanton', 'Pacific/Kanton'],
  ['atlantic/faeroe', 'Atlantic/Faroe'],
  ['atlantic/faroe', 'Atlantic/Faroe'],
  ['america/fort_wayne', 'America/Indiana/Indianapolis'],
  ['america/louisville', 'America/Kentucky/Louisville'],
  ['america/indiana/indianapolis', 'America/Indiana/Indianapolis'],
  ['america/kentucky/louisville', 'America/Kentucky/Louisville'],
  ['america/buenos_aires', 'America/Argentina/Buenos_Aires'],
  ['america/argentina/buenos_aires', 'America/Argentina/Buenos_Aires'],
])

const PREFERRED_ZONE_BY_RUNTIME_ID = buildPreferredZoneMap()

export function normalizeTimeZone(zone: string): string | null {
  const candidate = zone.trim()
  if (!candidate) return null
  const upperCandidate = candidate.toUpperCase()
  if (!candidate.includes('/') && !UNAMBIGUOUS_TIME_ZONE_ALIASES.has(upperCandidate)) return null
  if (AMBIGUOUS_TIME_ZONE_ABBREVIATIONS.has(upperCandidate)) return null
  const etcGmtZone = normalizeEtcGmtZone(candidate)
  if (etcGmtZone) return resolveRuntimeTimeZone(etcGmtZone) ? etcGmtZone : null
  const explicitAlias = EXPLICIT_TIME_ZONE_ALIASES.get(candidate.toLowerCase())
  if (explicitAlias) {
    return resolveRuntimeTimeZone(explicitAlias) ? explicitAlias : resolveRuntimeTimeZone(candidate)
  }
  const runtimeZone = resolveRuntimeTimeZone(candidate)
  if (!runtimeZone) return null
  // Some ICU versions expose a primary IANA ID through supportedValuesOf even
  // when the bundled link table also contains that spelling. Preserve the
  // primary identity before consulting the legacy-link fallback map.
  const supportedPrimary = supportedPrimaryTimeZoneId(candidate)
  if (supportedPrimary) return supportedPrimary
  const preferredZone = IANA_LINK_TO_PREFERRED.get(candidate.toLowerCase()) ??
    EXPLICIT_TIME_ZONE_ALIASES.get(runtimeZone.toLowerCase()) ??
    PREFERRED_ZONE_BY_RUNTIME_ID.get(runtimeZone.toLowerCase()) ??
    undefined
  if (preferredZone) return resolveRuntimeTimeZone(preferredZone) ? preferredZone : runtimeZone

  // Do not accept arbitrary IANA links merely because a browser preserves the
  // input in resolvedOptions(). Only primary IDs (or explicit aliases above)
  // have a stable cross-engine representation.
  return null
}

export function isValidTimeZone(zone: string): boolean {
  return normalizeTimeZone(zone) !== null
}

export function getDefaultZones(browserZone?: string): string[] {
  const detected = browserZone ? normalizeTimeZone(browserZone) ?? DEFAULT_ZONE : DEFAULT_ZONE
  return [...new Set([detected, 'Europe/Moscow', 'UTC'])].slice(0, 5)
}

export function resolveZonedInstant(
  date: string,
  time: string,
  zone: string,
  disambiguation: Disambiguation,
): ZonedInstantResult {
  const wallClock = parseWallClock(date, time)
  const normalizedZone = normalizeTimeZone(zone)
  if (!wallClock || !normalizedZone) {
    return { status: 'invalid', instants: [], instant: null }
  }

  const approximate = wallClockToUtcMillis(wallClock)
  const offsets = new Set<number>()
  for (const hours of [-36, -24, -12, 0, 12, 24, 36]) {
    const offset = offsetSecondsAt(approximate + hours * HOUR_MS, normalizedZone)
    if (offset !== null) offsets.add(offset)
  }

  const instants = [...offsets]
    .map((offset) => approximate - offset * 1_000)
    .filter((instant) => wallClockEquals(instant, normalizedZone, wallClock))
    .filter((instant, index, all) => all.indexOf(instant) === index)
    .sort((left, right) => left - right)

  if (instants.length === 0) return { status: 'gap', instants, instant: null }
  if (instants.length === 1) return { status: 'ready', instants, instant: instants[0]! }

  return {
    status: 'ambiguous',
    instants,
    instant: disambiguation === 'earlier' ? instants[0]! : instants.at(-1)!,
  }
}

export function parseSharedTimeZoneState(
  hash: string | undefined,
  browserZone?: string,
  now = new Date(),
): TimeZoneState {
  const fallbackZones = getDefaultZones(browserZone)
  const fallback: TimeZoneState = {
    date: localDateInput(now),
    time: localTimeInput(now),
    sourceZone: fallbackZones[0]!,
    zones: fallbackZones,
    disambiguation: 'earlier',
  }
  if (!hash?.startsWith('#')) return fallback
  if (hash.length > MAX_SHARED_STATE_LENGTH) return fallback

  const params = new URLSearchParams(hash.slice(1))
  const rawZones = params.get('zones') ?? ''
  const rawZoneTokens = rawZones ? rawZones.split(',') : []
  if (
    rawZoneTokens.length > MAX_SHARED_ZONE_TOKENS ||
    rawZoneTokens.some((zone) => zone.length > 128)
  ) return fallback

  const zones = rawZoneTokens
    .map(normalizeTimeZone)
    .filter((zone): zone is string => zone !== null)
    .filter((zone, index, all) => all.indexOf(zone) === index)
    .slice(0, MAX_SHARED_ZONE_TOKENS)
  const sourceZone = normalizeTimeZone(params.get('source') ?? '')
  const date = params.get('date') ?? ''
  const time = params.get('time') ?? ''
  const disambiguation = params.get('pick') === 'later' ? 'later' : 'earlier'

  return {
    date: parseWallClock(date, time) ? date : fallback.date,
    time: parseWallClock(date, time) ? time : fallback.time,
    sourceZone: sourceZone ?? fallback.sourceZone,
    zones: zones.length >= 2 ? zones : fallback.zones,
    disambiguation,
  }
}

export function getSsgInitialTimeZoneState(): TimeZoneState {
  return {
    date: SSG_DATE,
    time: SSG_TIME,
    sourceZone: 'UTC',
    zones: ['UTC', 'Europe/Moscow'],
    disambiguation: 'earlier',
  }
}

export function shouldHydrateTimeZoneFromBrowser(userAgent: string): boolean {
  return userAgent !== 'ReactSnap'
}

export function buildShareHash(state: TimeZoneState): string {
  const params = new URLSearchParams({
    date: state.date,
    time: state.time,
    source: state.sourceZone,
    zones: state.zones.join(','),
    pick: state.disambiguation,
  })
  return `#${params.toString()}`
}

export function formatOffset(instant: number, zone: string): string {
  const offset = offsetSecondsAt(instant, zone)
  if (offset === null || offset === 0) return 'UTC+00:00'
  const sign = offset >= 0 ? '+' : '-'
  const absolute = Math.abs(offset)
  const hours = Math.floor(absolute / 3_600)
  const minutes = Math.floor((absolute % 3_600) / 60)
  const seconds = absolute % 60
  const suffix = seconds === 0 ? '' : `:${String(seconds).padStart(2, '0')}`
  return `UTC${sign}${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}${suffix}`
}

export function formatInstant(instant: number, zone: string, locale: string): string {
  const offset = formatOffset(instant, zone)
  const hasSecondPrecision = new Date(instant).getUTCSeconds() !== 0 || /:\d{2}:\d{2}$/.test(offset)
  const eraParts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone, year: 'numeric', era: 'short',
  }).formatToParts(new Date(instant))
  const localEra = eraParts.find((part) => part.type === 'era')?.value
  const isBeforeCommonEra = localEra !== undefined && !['AD', 'CE'].includes(localEra)
  return new Intl.DateTimeFormat(locale, {
    timeZone: zone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
    ...(hasSecondPrecision ? { second: '2-digit' as const } : {}),
    ...(isBeforeCommonEra ? { era: 'long' as const } : {}),
    hourCycle: 'h23',
  }).format(new Date(instant))
}

function parseWallClock(date: string, time: string): WallClock | null {
  const dateMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  const timeMatch = /^(\d{2}):(\d{2})$/.exec(time)
  if (!dateMatch || !timeMatch) return null
  const values = {
    year: Number(dateMatch[1]), month: Number(dateMatch[2]), day: Number(dateMatch[3]),
    hour: Number(timeMatch[1]), minute: Number(timeMatch[2]), second: 0,
  }
  const probe = new Date(wallClockToUtcMillis(values))
  if (
    values.year < 1 || values.hour > 23 || values.minute > 59 ||
    probe.getUTCFullYear() !== values.year || probe.getUTCMonth() !== values.month - 1 ||
    probe.getUTCDate() !== values.day
  ) return null
  return values
}

function wallClockToUtcMillis(wallClock: WallClock): number {
  const date = new Date(0)
  date.setUTCFullYear(wallClock.year, wallClock.month - 1, wallClock.day)
  date.setUTCHours(wallClock.hour, wallClock.minute, 0, 0)
  return date.getTime()
}

function offsetSecondsAt(instant: number, zone: string): number | null {
  try {
    const part = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date(instant)).find((item) => item.type === 'timeZoneName')?.value
    if (!part || part === 'GMT' || part === 'UTC') return 0
    const match = /^GMT([+-])(\d{1,2})(?::(\d{2}))?(?::(\d{2}))?$/.exec(part)
    if (!match) return null
    const seconds = Number(match[2]) * 3_600 + Number(match[3] ?? 0) * 60 + Number(match[4] ?? 0)
    return match[1] === '+' ? seconds : -seconds
  } catch {
    return null
  }
}

function wallClockEquals(instant: number, zone: string, expected: WallClock): boolean {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(instant))
  const value = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value)
  return value('year') === expected.year && value('month') === expected.month &&
    value('day') === expected.day && value('hour') === expected.hour && value('minute') === expected.minute &&
    value('second') === expected.second
}

function localDateInput(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

function localTimeInput(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
}

function buildPreferredZoneMap(): ReadonlyMap<string, string> {
  const preferredByRuntimeId = new Map<string, string>()
  for (const [preferred, aliases] of PREFERRED_TIME_ZONE_ALIASES) {
    if (!resolveRuntimeTimeZone(preferred)) continue
    for (const candidate of [preferred, ...aliases]) {
      const runtimeZone = resolveRuntimeTimeZone(candidate)
      if (runtimeZone) preferredByRuntimeId.set(runtimeZone.toLowerCase(), preferred)
    }
  }
  return preferredByRuntimeId
}

function resolveRuntimeTimeZone(zone: string): string | null {
  try {
    return new Intl.DateTimeFormat('en', { timeZone: zone }).resolvedOptions().timeZone
  } catch {
    return null
  }
}

function supportedPrimaryTimeZoneId(candidate: string): string | null {
  const lowerCandidate = candidate.toLowerCase()
  // Keep the hand-maintained primary set authoritative for records such as
  // Antarctica/DumontDUrville that are also present in legacy compatibility
  // tables. The generated default-profile set is evaluated after Link records
  // so default tzdata aliases (for example Africa/Accra) cannot win merely
  // because Intl.supportedValuesOf() exposes their spelling.
  const primaryId = [...IANA_PRIMARY_TIME_ZONE_IDS]
    .find((value) => value.toLowerCase() === candidate.toLowerCase())
  if (primaryId) return primaryId
  if (IANA_LINK_TO_PREFERRED.has(lowerCandidate)) return null
  const tzdataPrimaryId = [...TZDATA_2026B_PRIMARY_TIME_ZONE_IDS]
    .find((value) => value.toLowerCase() === lowerCandidate)
  if (tzdataPrimaryId) return tzdataPrimaryId
  try {
    const supportedValuesOf = (Intl as typeof Intl & {
      supportedValuesOf?: (key: string) => string[]
    }).supportedValuesOf
    if (supportedValuesOf) {
      const lowerCandidate = candidate.toLowerCase()
      return supportedValuesOf('timeZone').find((value) => value.toLowerCase() === lowerCandidate) ?? null
    }
  } catch {
    return null
  }
  return resolveRuntimeTimeZone(candidate) === candidate ? candidate : null
}

function normalizeEtcGmtZone(candidate: string): string | null {
  const match = /^etc\/gmt([+-])(\d{1,2})$/i.exec(candidate)
  if (!match) return null
  const hours = Number(match[2])
  const maxHours = match[1] === '+' ? 12 : 14
  if (hours > maxHours) return null
  if (hours === 0) return 'UTC'
  return `Etc/GMT${match[1]}${hours}`
}
